import moment from 'moment';

const formattedWays = {
  departs: 'Departure',
  returns: 'Return',
};

/**
 * Returns whether a flight with connections has several carriers
 * @method hasMultipleCarriers
 * @param {Array} legs Flight connections
 * @return {Boolean}
 */
function hasMultipleCarriers(legs) {
  if (legs.length === 1) return false;

  const firstCarrier = legs[0].carrier.name;

  return !legs.every((leg) => leg.carrier.name === firstCarrier);
}

function getMulticarrierProperties(legs) {
  if (!hasMultipleCarriers(legs)) return {};

  let isAlly = true;
  let isProtected = false;
  let multicarrierNames = [];

  legs.forEach(({ carrier }) => {
    const { name, ally, copyrightProtected } = carrier;

    isAlly = isAlly || ally;
    isProtected = isProtected && copyrightProtected;
    multicarrierNames.push(name);
  });

  multicarrierNames = multicarrierNames.join();

  return {
    'Ally': !!isAlly,
    'Copyright Protected': !!isProtected,
    'Line': 'Multicarrier Price Travel',
    'Multicarrier Lines': multicarrierNames,
    'Multicarrier Transporters': multicarrierNames,
    'Transporter': 'Multicarrier Price Travel',
  };
}

function getBusProperties(trip) {
  const {
    carrier: { name, buslineGroup, ally, copyrightProtected },
    stops,
  } = trip;

  return {
    'Ally': ally,
    'Copyright Protected': copyrightProtected,
    'Line': name,
    'Stops': parseInt(stops, 10),
    'Transporter': buslineGroup.name,
  };
}

function getFlightProperties(flight) {
  const {
    carrier: { name, ally, copyrightProtected },
    stops,
    legs,
  } = flight;
  let stopCities;

  if (stops > 0) {
    stopCities = legs
      .map((leg) => leg.destination.city.name)
      .slice(0, stops)
      .join();
  }

  return {
    'Ally': ally,
    'Copyright Protected': !!copyrightProtected,
    'Line': name,
    'Stops': stops,
    'Stop Cities': stopCities,
    'Transporter': name,
    ...getMulticarrierProperties(legs),
  };
}

function getMixedProperties(itinerary) {
  const { fragments } = itinerary;
  let totalStops = fragments.length - 1;
  const stopCities = [];
  const lines = [];
  const transporters = [];
  const transportTypes = [];
  let isAlly = true;
  let isProtected = false;

  fragments.forEach((fragment) => {
    const {
      transportType,
      stops,
      legs,
      destination,
      carrier: { name, buslineGroup, ally, copyrightProtected },
    } = fragment;
    let fragmentAlly = true;
    let fragmentProtected = false;

    if (transportType === 'bus') {
      stopCities.push(destination.city.name);
      lines.push(name);
      transporters.push(buslineGroup.name);
      fragmentAlly = ally;
      fragmentProtected = copyrightProtected;
    } else {
      totalStops += stops;
      legs.forEach((leg) => {
        const legCarrier = leg.carrier;

        stopCities.push(leg.destination.city.name);
        lines.push(legCarrier.name);
        transporters.push(legCarrier.name);
        fragmentAlly = fragmentAlly && legCarrier.ally;
        fragmentProtected = fragmentProtected || leg.copyrightProtected;
      });
    }

    isAlly = isAlly && fragmentAlly;
    isProtected = isProtected || fragmentProtected;
    transportTypes.push(transportType);
  });

  return {
    'Ally': isAlly,
    'Copyright Protected': !!isProtected,
    'Line': 'Multicarrier',
    'Multicarrier Lines': lines.join(),
    'Multicarrier Transporters': transporters.join(),
    'Multicarrier Transport Types': transportTypes.join(),
    'Stops': totalStops,
    'Stop Cities': stopCities.slice(0, totalStops).join(),
    'Transporter': 'Multicarrier',
  };
}

export default function serializeTrip(
  transportType,
  trip,
  way,
  openTicket = false
) {
  const { departure, arrival, pricing, origin, destination } = trip;
  const originName = origin.name;
  const originCityName = origin.city.name;
  const destinationName = destination.name;
  const destinationCityName = destination.city.name;
  let transportProperties;

  switch (transportType) {
    case 'bus':
      transportProperties = getBusProperties(trip);
      break;
    case 'flight':
      transportProperties = getFlightProperties(trip);
      break;
    case 'mixed':
      transportProperties = getMixedProperties(trip);
      break;
    default:
      transportProperties = {};
  }

  return {
    'Arrival': openTicket ? null : moment(arrival).format(),
    'Departure': openTicket ? null : moment(departure).format(),
    'Destination': destinationCityName,
    'Destination Terminal': destinationName,
    'Origin': originCityName,
    'Origin Terminal': originName,
    'Price': pricing.total,
    'Route': `${originCityName} - ${destinationCityName}`,
    'Transport Type': transportType,
    'Way': formattedWays[way],
    'Open Ticket': openTicket,
    ...transportProperties,
  };
}



// WEBPACK FOOTER //
// ./serializers/trip.js